import { generateClient } from 'aws-amplify/api'
import {
  listTasks as listTasksQuery,
  myTasks as myTasksQuery,
  getTaskLeads as getTaskLeadsQuery,
  getTaskAssignmentUploadStatus as getTaskAssignmentUploadStatusQuery,
  getTaskMetrics as getTaskMetricsQuery,
  getTaskMetricsDrilldown as getTaskMetricsDrilldownQuery,
  getTaskStatusMetrics as getTaskStatusMetricsQuery,
  getScopeMetrics as getScopeMetricsQuery,
  getScopeMetricsDrilldown as getScopeMetricsDrilldownQuery,
  getScopeStatusMetrics as getScopeStatusMetricsQuery,
  startReportExport as startReportExportQuery,
  getReportExportStatus as getReportExportStatusQuery,
  listSlotResources as listSlotResourcesQuery,
  listSlotBookings as listSlotBookingsQuery,
} from '@/graphql/queries'
import {
  createTask as createTaskMutation,
  editTask as editTaskMutation,
  deleteTask as deleteTaskMutation,
  createSlotResource as createSlotResourceMutation,
  updateSlotResource as updateSlotResourceMutation,
  deleteSlotResource as deleteSlotResourceMutation,
  cancelSlotBooking as cancelSlotBookingMutation,
  assignLeadToAgent as assignLeadToAgentMutation,
  bulkAssignLeadsToAgents as bulkAssignLeadsToAgentsMutation,
  updateMyTaskLeadStatus as updateMyTaskLeadStatusMutation,
  bulkUpdateTaskLeadStatus as bulkUpdateTaskLeadStatusMutation,
  bulkUpdateAllTaskLeadStatus as bulkUpdateAllTaskLeadStatusMutation,
  runTaskAICalls as runTaskAICallsMutation,
  runPublicApiTest as runPublicApiTestMutation,
  getTaskVoiceAudioUploadUrl as getTaskVoiceAudioUploadUrlMutation,
  getTaskVoiceAudioPreviewUrl as getTaskVoiceAudioPreviewUrlMutation,
  updateTaskReportDigestConfig as updateTaskReportDigestConfigMutation,
  triggerReportDigestNow as triggerReportDigestNowMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { resolveCampaignLeadsPayload } from '@/utils/leadShards'
import {
  assignmentsToCsv,
  normalizeAssignmentUploadStatus,
} from '@/utils/excelAssignments'

const client = generateClient()

function resolveCampaignId(input = {}) {
  return String(
    input.campaign_id ??
      input.campaignId ??
      input.task_id ??
      input.taskId ??
      '',
  ).trim()
}

/** HH:MM 24-hour string for working_hours_* fields. */
function normalizeWorkingHoursTime(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const match = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/)
  if (!match) return ''
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

function appendAutoDialScheduleFields(payload, input = {}) {
  const start = normalizeWorkingHoursTime(
    input.working_hours_start ?? input.workingHoursStart,
  )
  if (start) payload.working_hours_start = start

  const end = normalizeWorkingHoursTime(
    input.working_hours_end ?? input.workingHoursEnd,
  )
  if (end) payload.working_hours_end = end

  // Campaign-level retry spacing override -- see
  // effectiveRetryCooldownMinutes server-side. Checks presence rather than
  // going through `??` (which would turn an explicit null back into
  // undefined whenever the sibling camelCase key is absent, silently
  // swallowing the clear) so clearing it back to "use the platform floor"
  // is a real choice that actually reaches the backend, same reasoning as
  // max_dial_attempts/daily_dial_cap below. No longer auto_dial-gated --
  // this function runs for every campaign regardless, and a manually-run
  // campaign is redialed by "Run AI calls" just as much as auto-dial redials
  // one, so it needs the same spacing control.
  const hasRetryCooldownInput =
    input.retry_cooldown_minutes !== undefined || input.retryCooldownMinutes !== undefined
  if (hasRetryCooldownInput) {
    const rawCooldown =
      input.retry_cooldown_minutes !== undefined
        ? input.retry_cooldown_minutes
        : input.retryCooldownMinutes
    payload.retry_cooldown_minutes =
      rawCooldown === null || rawCooldown === '' ? null : Math.floor(Number(rawCooldown))
  }

  // Was missing entirely -- this rebuild step silently dropped it before
  // the request ever reached the backend, so "Max dial attempts" in the
  // Settings tab has never actually saved (confirmed live 2026-08-13,
  // found while auditing this same function for daily_dial_cap below).
  // null (via CampaignAdvancedSettings' own `? Number(...) : null`) is a
  // real "clear it back to the platform default" choice, so this checks
  // input.max_dial_attempts !== undefined rather than truthiness.
  const maxDialAttemptsInput = input.max_dial_attempts ?? input.maxDialAttempts
  if (maxDialAttemptsInput !== undefined) {
    payload.max_dial_attempts = maxDialAttemptsInput === null ? null : Number(maxDialAttemptsInput)
  }

  // Campaign-level override of the platform's per-lead daily dial ceiling
  // -- see effectiveDailyDialCap server-side. Same clear-with-null pattern.
  const dailyDialCapInput = input.daily_dial_cap ?? input.dailyDialCap
  if (dailyDialCapInput !== undefined) {
    payload.daily_dial_cap = dailyDialCapInput === null ? null : Number(dailyDialCapInput)
  }

  // dial_ratio (Int 1–10) — HUMAN_AGENT + auto_dial only.
  // Live phone system is not ready for ratio > 1; always persist 1 for now.
  appendDialRatioField(payload, input)
}

/**
 * Dashboard Digest fields (createTask / editTask) — periodic KPI +
 * agent-activity email/WhatsApp summary, off by default per campaign.
 * Shared between createCampaign and editCampaign, same shape as
 * appendAutoDialScheduleFields above.
 */
function appendDashboardDigestFields(payload, input = {}) {
  if (input.send_dashboard_digest_by_email != null || input.sendDashboardDigestByEmail != null) {
    payload.send_dashboard_digest_by_email = Boolean(
      input.send_dashboard_digest_by_email ?? input.sendDashboardDigestByEmail,
    )
  }
  const emailAddresses = input.dashboard_digest_email_addresses ?? input.dashboardDigestEmailAddresses
  if (Array.isArray(emailAddresses)) payload.dashboard_digest_email_addresses = emailAddresses

  if (input.send_dashboard_digest_by_whatsapp != null || input.sendDashboardDigestByWhatsapp != null) {
    payload.send_dashboard_digest_by_whatsapp = Boolean(
      input.send_dashboard_digest_by_whatsapp ?? input.sendDashboardDigestByWhatsapp,
    )
  }
  const whatsappNumbers = input.dashboard_digest_whatsapp_numbers ?? input.dashboardDigestWhatsappNumbers
  if (Array.isArray(whatsappNumbers)) payload.dashboard_digest_whatsapp_numbers = whatsappNumbers

  if (input.dashboard_digest_cc_agents != null || input.dashboardDigestCcAgents != null) {
    payload.dashboard_digest_cc_agents = Boolean(
      input.dashboard_digest_cc_agents ?? input.dashboardDigestCcAgents,
    )
  }
  const digestTimes = input.dashboard_digest_times ?? input.dashboardDigestTimes
  if (Array.isArray(digestTimes)) payload.dashboard_digest_times = digestTimes

  // Independent weekly/monthly cadences alongside the daily times above.
  if (input.dashboard_digest_weekly_enabled != null || input.dashboardDigestWeeklyEnabled != null) {
    payload.dashboard_digest_weekly_enabled = Boolean(
      input.dashboard_digest_weekly_enabled ?? input.dashboardDigestWeeklyEnabled,
    )
  }
  const weeklyDay = input.dashboard_digest_weekly_day ?? input.dashboardDigestWeeklyDay
  if (weeklyDay != null) payload.dashboard_digest_weekly_day = weeklyDay
  const weeklyTime = input.dashboard_digest_weekly_time ?? input.dashboardDigestWeeklyTime
  if (weeklyTime != null) payload.dashboard_digest_weekly_time = weeklyTime

  if (input.dashboard_digest_monthly_enabled != null || input.dashboardDigestMonthlyEnabled != null) {
    payload.dashboard_digest_monthly_enabled = Boolean(
      input.dashboard_digest_monthly_enabled ?? input.dashboardDigestMonthlyEnabled,
    )
  }
  const monthlyDay = input.dashboard_digest_monthly_day ?? input.dashboardDigestMonthlyDay
  if (monthlyDay != null) payload.dashboard_digest_monthly_day = monthlyDay
  const monthlyTime = input.dashboard_digest_monthly_time ?? input.dashboardDigestMonthlyTime
  if (monthlyTime != null) payload.dashboard_digest_monthly_time = monthlyTime
}

/**
 * Softphone predictive dial_ratio (createTask / editTask Int).
 * Set ALLOW_SOFTPHONE_DIAL_RATIO_GT_1 when parallel connect queuing is ready.
 */
const ALLOW_SOFTPHONE_DIAL_RATIO_GT_1 = false

function appendDialRatioField(payload, input = {}) {
  const handledBy = String(
    input.handled_by ?? input.handledBy ?? payload.handled_by ?? '',
  )
    .trim()
    .toUpperCase()
  const autoDial =
    input.auto_dial_enabled === true ||
    input.autoDialEnabled === true ||
    payload.auto_dial_enabled === true

  // Never leave a stale dial_ratio on the payload for AI / non-autodial tasks.
  if (handledBy !== 'HUMAN_AGENT' || !autoDial) {
    delete payload.dial_ratio
    return
  }

  const raw = Number(input.dial_ratio ?? input.dialRatio ?? payload.dial_ratio ?? 1)
  const clamped = Number.isFinite(raw)
    ? Math.min(10, Math.max(1, Math.floor(raw)))
    : 1

  // Live-safe: always send 1 until phone-system queuing supports parallel connects.
  payload.dial_ratio =
    ALLOW_SOFTPHONE_DIAL_RATIO_GT_1 && clamped > 1 ? clamped : 1
}

async function runCampaignsField(document, field, input) {
  try {
    const variables = input == null ? undefined : { input }
    const { data, errors } = await client.graphql({
      query: document,
      ...(variables ? { variables } : {}),
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return parseAwsJson(data?.[field])
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Request failed'),
    }
  }
}

/** All campaigns in the org — ADMIN/OWNER view. */
export function fetchCampaigns() {
  return runCampaignsField(listTasksQuery, 'listTasks')
}

/** Campaigns assigned to the calling agent. */
export function fetchMyCampaigns() {
  return runCampaignsField(myTasksQuery, 'myTasks')
}

/**
 * Leads queued in a campaign (with per-lead agent assignment).
 * GraphQL returns S3 coordinates + membership; this joins shard data.
 */
export async function fetchCampaignLeads(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
  }
  if (input.all_leads === true || input.allLeads === true) {
    payload.all_leads = true
  } else if (input.all_leads === false || input.allLeads === false) {
    payload.all_leads = false
  }

  const response = await runCampaignsField(getTaskLeadsQuery, 'getTaskLeads', payload)
  if (!isApiSuccess(response)) return response

  try {
    const data = response?.data ?? response
    const { items, campaign } = await resolveCampaignLeadsPayload(data)
    return {
      status: 'SUCCESS',
      message: response?.message,
      data: {
        items,
        campaign,
      },
    }
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Failed to download campaign lead shards from S3'),
    }
  }
}

/**
 * Create a calling campaign. GraphQL still uses createTask / task_* fields.
 * EXCEL mode: PUT CSV to upload_url, then poll getTaskAssignmentUploadStatus.
 * FIELD_BASED mode: poll getTaskAssignmentUploadStatus (no file upload).
 */
export function createCampaign(input = {}) {
  const payload = {
    task_name: String(input.task_name ?? input.campaign_name ?? '').trim(),
    context_names: (Array.isArray(input.context_names) ? input.context_names : [])
      .map((name) => String(name ?? '').trim())
      .filter(Boolean),
    assigned_agent_ids: (Array.isArray(input.assigned_agent_ids)
      ? input.assigned_agent_ids
      : []
    )
      .map((id) => String(id ?? '').trim())
      .filter(Boolean),
    distribution_mode: String(input.distribution_mode ?? '').trim().toUpperCase(),
    handled_by: String(input.handled_by ?? '').trim().toUpperCase(),
  }

  const greeting = String(input.greeting ?? '').trim()
  if (greeting) payload.greeting = greeting

  // Optional draft -- the backend polishes it via AI rather than storing
  // it verbatim (see generateTaskMissionPrompt's user_draft param). Omitted
  // entirely means "generate purely from campaign name/description," same
  // as before this existed.
  const missionPromptDraft = String(input.mission_prompt ?? '').trim()
  if (missionPromptDraft) payload.mission_prompt = missionPromptDraft

  // Tri-state override of the org's own disclaimer setting -- undefined
  // (the field was never touched) omits it entirely, so the campaign
  // simply inherits the org default, same as before this existed.
  if (input.disclaimer_enabled !== undefined) {
    payload.disclaimer_enabled = input.disclaimer_enabled === null ? null : Boolean(input.disclaimer_enabled)
  }

  // RECURRING_REMINDER campaigns have no start/end date -- they run
  // indefinitely on a per-lead cycle (the structured schedule_* fields)
  // instead. STANDARD campaigns keep the existing required window.
  const taskType = String(input.task_type ?? '').trim().toUpperCase()
  if (taskType) payload.task_type = taskType
  // Create-only: direction is immutable once a campaign exists, so editTask
  // rejects any attempt to change it (see its own comment server-side).
  if (taskType === 'RECURRING_REMINDER') {
    payload.anchor_field = String(input.anchor_field ?? '').trim()
    // Fixed calendar date (ONE_TIME only) -- was missing here entirely,
    // so a campaign built with Date-to-call's "Same for every lead" mode
    // (anchor_field left blank by design, only anchor_date holds the
    // value) silently lost it at this rebuild step and reached the
    // backend with BOTH empty, always failing validation (confirmed live
    // 2026-08-13, Samsung Outbound C5).
    payload.anchor_date = String(input.anchor_date ?? '').trim()
    payload.schedule_type = String(input.schedule_type ?? '').trim().toUpperCase()
    payload.schedule_frequency = Number(input.schedule_frequency)
    payload.schedule_time = String(input.schedule_time ?? '').trim()
    const scheduleTypeField = String(input.schedule_type_field ?? '').trim()
    if (scheduleTypeField) payload.schedule_type_field = scheduleTypeField
    const scheduleFrequencyField = String(input.schedule_frequency_field ?? '').trim()
    if (scheduleFrequencyField) payload.schedule_frequency_field = scheduleFrequencyField
    const scheduleTimeField = String(input.schedule_time_field ?? '').trim()
    if (scheduleTimeField) payload.schedule_time_field = scheduleTimeField
    if (payload.schedule_type === 'WEEKLY') {
      payload.schedule_day_of_week = String(input.schedule_day_of_week ?? '').trim().toUpperCase()
      const scheduleDayOfWeekField = String(input.schedule_day_of_week_field ?? '').trim()
      if (scheduleDayOfWeekField) payload.schedule_day_of_week_field = scheduleDayOfWeekField
    }
    if (payload.schedule_type === 'MONTHLY') {
      payload.schedule_day_of_month = String(input.schedule_day_of_month ?? '').trim()
      const scheduleDayOfMonthField = String(input.schedule_day_of_month_field ?? '').trim()
      if (scheduleDayOfMonthField) payload.schedule_day_of_month_field = scheduleDayOfMonthField
    }
  } else {
    payload.start_date = String(input.start_date ?? '').trim()
    payload.end_date = String(input.end_date ?? '').trim()
  }

  const description = String(input.description ?? '').trim()
  if (description) payload.description = description

  if (Array.isArray(input.filters) && input.filters.length) {
    payload.filters = input.filters
  }

  const agentField = String(input.agent_field ?? input.agentField ?? '').trim()
  if (agentField) payload.agent_field = agentField

  if (input.auto_dial_enabled != null) {
    payload.auto_dial_enabled = Boolean(input.auto_dial_enabled)
  }
  if (input.restrict_to_known_callers != null) {
    payload.restrict_to_known_callers = Boolean(input.restrict_to_known_callers)
  }
  appendAutoDialScheduleFields(payload, input)

  if (input.auto_adopt_matching_leads != null || input.autoAdoptMatchingLeads != null) {
    payload.auto_adopt_matching_leads = Boolean(
      input.auto_adopt_matching_leads ?? input.autoAdoptMatchingLeads,
    )
  }

  if (input.only_future_leads != null || input.onlyFutureLeads != null) {
    payload.only_future_leads = Boolean(
      input.only_future_leads ?? input.onlyFutureLeads,
    )
  }

  appendDashboardDigestFields(payload, input)

  return runCampaignsField(createTaskMutation, 'createTask', payload)
}

/**
 * Edit a calling campaign (editTask). Merge-only for lead membership —
 * changing contexts / distribution / filters / agent_field re-runs selection
 * asynchronously (PROCESSING + upload_id / upload_url), same as create.
 */
export function editCampaign(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
  }
  if (!payload.task_id) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Campaign id is required to edit.',
    })
  }

  const taskName = String(input.task_name ?? input.campaign_name ?? '').trim()
  if (taskName) payload.task_name = taskName

  if (input.description != null) {
    payload.description = String(input.description ?? '').trim()
  }

  const startDate = String(input.start_date ?? '').trim()
  if (startDate) payload.start_date = startDate
  const endDate = String(input.end_date ?? '').trim()
  if (endDate) payload.end_date = endDate
  // task_type is immutable after creation (not sent on edit at all) --
  // the structured schedule_* fields ARE editable for an existing
  // RECURRING_REMINDER campaign. Distinguish "not touched" (omitted,
  // undefined) from "explicitly cleared" (empty string/null) for every
  // "_field" override the same way anchor_field always has -- the latter
  // must actually send null so the backend clears a previously-set
  // override, not just skip sending the key and leave the old value in
  // place.
  if (input.anchor_field !== undefined) {
    const anchorField = String(input.anchor_field ?? '').trim()
    payload.anchor_field = anchorField || null
  }
  if (input.anchor_date !== undefined) {
    const anchorDate = String(input.anchor_date ?? '').trim()
    payload.anchor_date = anchorDate || null
  }
  if (input.schedule_type !== undefined) {
    payload.schedule_type = String(input.schedule_type ?? '').trim().toUpperCase() || null
  }
  if (input.schedule_frequency != null) {
    payload.schedule_frequency = Number(input.schedule_frequency)
  }
  if (input.schedule_time !== undefined) {
    payload.schedule_time = String(input.schedule_time ?? '').trim() || null
  }
  if (input.schedule_type_field !== undefined) {
    payload.schedule_type_field = String(input.schedule_type_field ?? '').trim() || null
  }
  if (input.schedule_frequency_field !== undefined) {
    payload.schedule_frequency_field = String(input.schedule_frequency_field ?? '').trim() || null
  }
  if (input.schedule_time_field !== undefined) {
    payload.schedule_time_field = String(input.schedule_time_field ?? '').trim() || null
  }
  if (input.schedule_day_of_week !== undefined) {
    payload.schedule_day_of_week = String(input.schedule_day_of_week ?? '').trim().toUpperCase() || null
  }
  if (input.schedule_day_of_week_field !== undefined) {
    payload.schedule_day_of_week_field = String(input.schedule_day_of_week_field ?? '').trim() || null
  }
  if (input.schedule_day_of_month !== undefined) {
    payload.schedule_day_of_month = String(input.schedule_day_of_month ?? '').trim() || null
  }
  if (input.schedule_day_of_month_field !== undefined) {
    payload.schedule_day_of_month_field = String(input.schedule_day_of_month_field ?? '').trim() || null
  }
  const editGreeting = String(input.greeting ?? '').trim()
  if (editGreeting) payload.greeting = editGreeting
  const editMissionPrompt = String(input.mission_prompt ?? '').trim()
  if (editMissionPrompt) payload.mission_prompt = editMissionPrompt
  // Explicit null clears a campaign's own override back to inheriting the
  // org default -- unlike greeting/mission_prompt above, false is a
  // meaningful value here, so this can't use the same truthy-string check.
  if (input.disclaimer_enabled !== undefined) {
    payload.disclaimer_enabled = input.disclaimer_enabled === null ? null : Boolean(input.disclaimer_enabled)
  }

  // Voice mode: AI Speak (default) vs Static Voice (dial, play a
  // pre-resolved message, hang up -- no AI engine). Sent whenever
  // CampaignAdvancedSettings' Voice mode section has touched any of these,
  // same "present on input -> send it" convention as every other field
  // here -- the backend does its own STATIC_VOICE-specific validation.
  if (input.voice_mode !== undefined) {
    payload.voice_mode = String(input.voice_mode ?? '').trim().toUpperCase() || 'AI_SPEAK'
  }
  if (input.voice_source !== undefined) {
    payload.voice_source = String(input.voice_source ?? '').trim().toUpperCase() || null
  }
  if (input.voice_personalization !== undefined) {
    payload.voice_personalization = String(input.voice_personalization ?? '').trim().toUpperCase() || null
  }
  if (input.voice_script_text !== undefined) {
    payload.voice_script_text = String(input.voice_script_text ?? '').trim() || null
  }
  if (input.voice_audio_raw_key !== undefined) {
    payload.voice_audio_raw_key = String(input.voice_audio_raw_key ?? '').trim() || null
  }
  if (input.voice_audio_filename !== undefined) {
    payload.voice_audio_filename = String(input.voice_audio_filename ?? '').trim() || null
  }
  if (input.voice_language !== undefined) {
    payload.voice_language = String(input.voice_language ?? '').trim() || null
  }

  // AI-to-AI transfer (KAN-44): opt-in flag, same "present on input -> send
  // it" convention as everything else here. Explicit boolean, same shape as
  // disclaimer_enabled above -- false is a meaningful value (turning
  // eligibility back off), not "leave unchanged".
  if (input.ai_transfer_eligible !== undefined) {
    payload.ai_transfer_eligible = Boolean(input.ai_transfer_eligible)
  }

  // Was missing entirely -- CampaignAdvancedSettings' whole "Capture agreed
  // time" section (including the new date/time split) silently never
  // reached the backend on save, since this rebuild step dropped all three
  // keys before the request ever left the browser. Sent whenever present on
  // input (which CampaignAdvancedSettings always does for an AI campaign),
  // explicit empty -> null so clearing the field actually clears it rather
  // than leaving the old value in place.
  if (input.capture_datetime_field !== undefined) {
    payload.capture_datetime_field = String(input.capture_datetime_field ?? '').trim() || null
  }
  if (input.capture_datetime_time_field !== undefined) {
    payload.capture_datetime_time_field = String(input.capture_datetime_time_field ?? '').trim() || null
  }
  if (input.capture_datetime_prompt !== undefined) {
    payload.capture_datetime_prompt = String(input.capture_datetime_prompt ?? '').trim() || null
  }

  // Slot booking (2026-09-04): same "present on input -> send it" convention
  // as capture_datetime_* above -- CampaignAdvancedSettings' Slot booking
  // section always sends these when touched, so undefined here only ever
  // means the section itself was never rendered (e.g. a non-AI campaign).
  if (input.slot_booking_enabled !== undefined) {
    payload.slot_booking_enabled = Boolean(input.slot_booking_enabled)
  }
  if (input.slot_booking_notify_whatsapp_enabled !== undefined) {
    payload.slot_booking_notify_whatsapp_enabled = Boolean(input.slot_booking_notify_whatsapp_enabled)
  }
  if (input.slot_booking_notify_email_enabled !== undefined) {
    payload.slot_booking_notify_email_enabled = Boolean(input.slot_booking_notify_email_enabled)
  }
  if (input.slot_booking_confirmation_message !== undefined) {
    payload.slot_booking_confirmation_message = String(input.slot_booking_confirmation_message ?? '').trim() || null
  }

  if (Array.isArray(input.context_names)) {
    payload.context_names = input.context_names
      .map((name) => String(name ?? '').trim())
      .filter(Boolean)
  }

  if (Array.isArray(input.assigned_agent_ids)) {
    payload.assigned_agent_ids = input.assigned_agent_ids
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  }

  // Sent even when empty: clearing the list means "stop offering a
  // transfer on this campaign", which an omitted field would read as
  // "leave unchanged".
  if (Array.isArray(input.redirect_targets)) {
    payload.redirect_targets = input.redirect_targets
      .map((t) => String(t ?? '').trim())
      .filter(Boolean)
  }

  // Blank clears back to the platform default handover line -- same
  // empty-string-to-null convention as capture_datetime_prompt below.
  if (input.redirect_message !== undefined) {
    payload.redirect_message = String(input.redirect_message ?? '').trim() || null
  }

  // Blank falls back to the campaign's own name in the router prompt.
  if (input.redirect_description !== undefined) {
    payload.redirect_description = String(input.redirect_description ?? '').trim() || null
  }

  const distributionMode = String(input.distribution_mode ?? '').trim().toUpperCase()
  if (distributionMode) payload.distribution_mode = distributionMode

  const handledBy = String(input.handled_by ?? '').trim().toUpperCase()
  if (handledBy) payload.handled_by = handledBy

  if (Array.isArray(input.filters)) {
    payload.filters = input.filters
  }

  if (input.agent_field != null || input.agentField != null) {
    payload.agent_field = String(input.agent_field ?? input.agentField ?? '').trim()
  }

  if (input.auto_dial_enabled != null) {
    payload.auto_dial_enabled = Boolean(input.auto_dial_enabled)
  }
  if (input.restrict_to_known_callers != null) {
    payload.restrict_to_known_callers = Boolean(input.restrict_to_known_callers)
  }
  appendAutoDialScheduleFields(payload, input)

  if (input.auto_adopt_matching_leads != null || input.autoAdoptMatchingLeads != null) {
    payload.auto_adopt_matching_leads = Boolean(
      input.auto_adopt_matching_leads ?? input.autoAdoptMatchingLeads,
    )
  }

  // Only takes effect if this same edit also changes lead
  // distribution/products/filters -- backend no-ops it otherwise.
  if (input.only_future_leads != null || input.onlyFutureLeads != null) {
    payload.only_future_leads = Boolean(
      input.only_future_leads ?? input.onlyFutureLeads,
    )
  }

  appendDashboardDigestFields(payload, input)

  return runCampaignsField(editTaskMutation, 'editTask', payload)
}

/**
 * Pause a running campaign, or resume a paused one.
 *
 * Sends only the campaign id and the new status -- editTask leaves every field
 * it is not given exactly as it was, so a pause cannot quietly rewrite the
 * campaign's schedule, filters or agents as a side effect. The auto-dial
 * setting in particular is preserved, which is what lets Resume put back what
 * was actually running rather than a guess at it.
 */
export function setCampaignStatus(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Campaign id is required to change status.',
    })
  }
  const campaign_status = String(input.status ?? input.campaign_status ?? '')
    .trim()
    .toUpperCase()
  if (campaign_status !== 'PAUSED' && campaign_status !== 'ACTIVE') {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Status must be either PAUSED or ACTIVE.',
    })
  }
  return runCampaignsField(editTaskMutation, 'editTask', { task_id, campaign_status })
}

/**
 * Permanently delete a PAUSED campaign and everything carrying its id.
 *
 * Leads themselves survive: a lead usually sits on several campaigns, so only
 * this campaign's membership, metrics, call sessions and history entries go.
 * The backend refuses unless the campaign is paused.
 */
export function deleteCampaign(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required to delete.' })
  }
  return runCampaignsField(deleteTaskMutation, 'deleteTask', { task_id })
}

/** Weekday keys the backend's hours_<dow> fields use -- see createSlotResource server-side. */
export const SLOT_WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

function slotResourceHoursPayload(input = {}) {
  const payload = {}
  for (const dow of SLOT_WEEKDAYS) {
    const key = `hours_${dow}`
    if (input[key] !== undefined) {
      payload[key] = String(input[key] ?? '').trim() || null
    }
  }
  return payload
}

/** Bookable resources (doctors, rooms, staff) configured on a slot-booking campaign. */
export function fetchSlotResources(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required.' })
  }
  return runCampaignsField(listSlotResourcesQuery, 'listSlotResources', { task_id })
}

/**
 * Real confirmed bookings for a slot-booking campaign, merged across all of
 * its resources and sorted by time. Defaults server-side to a [-7d, +90d]
 * window; pass range_start_iso/range_end_iso to widen it.
 */
export function fetchSlotBookings(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required.' })
  }
  const payload = { task_id }
  if (input.resource_id) payload.resource_id = String(input.resource_id).trim()
  if (input.range_start_iso) payload.range_start_iso = input.range_start_iso
  if (input.range_end_iso) payload.range_end_iso = input.range_end_iso
  return runCampaignsField(listSlotBookingsQuery, 'listSlotBookings', payload)
}

export function createSlotResource(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required.' })
  }
  const resourceName = String(input.resource_name ?? '').trim()
  if (!resourceName) {
    return Promise.resolve({ status: 'ERROR', message: 'Resource name is required.' })
  }
  const payload = {
    task_id,
    resource_name: resourceName,
    slot_duration_minutes: Number(input.slot_duration_minutes) || 30,
    max_concurrent: Number(input.max_concurrent) || 1,
    is_active: input.is_active !== false,
    ...slotResourceHoursPayload(input),
  }
  return runCampaignsField(createSlotResourceMutation, 'createSlotResource', payload)
}

export function updateSlotResource(input = {}) {
  const resource_id = String(input.resource_id ?? '').trim()
  if (!resource_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Resource id is required.' })
  }
  const payload = { resource_id, ...slotResourceHoursPayload(input) }
  if (input.resource_name !== undefined) {
    payload.resource_name = String(input.resource_name ?? '').trim()
  }
  if (input.slot_duration_minutes !== undefined) {
    payload.slot_duration_minutes = Number(input.slot_duration_minutes)
  }
  if (input.max_concurrent !== undefined) {
    payload.max_concurrent = Number(input.max_concurrent)
  }
  if (input.is_active !== undefined) {
    payload.is_active = Boolean(input.is_active)
  }
  return runCampaignsField(updateSlotResourceMutation, 'updateSlotResource', payload)
}

export function deleteSlotResource(input = {}) {
  // Accepts either a plain resource_id string or { resource_id }. Was
  // `input.resource_id ?? input` -- for a bare {} (the default for a
  // no-args call, or any object missing resource_id), that fell through to
  // `input` itself, and String({}) is "[object Object]", a truthy string
  // that slipped past the empty check below and would have been sent to
  // the backend as a real resource_id instead of raising a clean error.
  const resource_id = String(
    typeof input === 'string' ? input : (input?.resource_id ?? ''),
  ).trim()
  if (!resource_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Resource id is required.' })
  }
  return runCampaignsField(deleteSlotResourceMutation, 'deleteSlotResource', { resource_id })
}

/** Releases a booked slot -- marks it cancelled and frees its capacity so it can be booked again. */
export function cancelSlotBooking(input = {}) {
  const booking_id = String(
    typeof input === 'string' ? input : (input?.booking_id ?? ''),
  ).trim()
  if (!booking_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Booking id is required.' })
  }
  return runCampaignsField(cancelSlotBookingMutation, 'cancelSlotBooking', { booking_id })
}

/** Poll Excel / FIELD_BASED assignment progress after create or edit. */
export function fetchCampaignAssignmentUploadStatus(input = {}) {
  return runCampaignsField(
    getTaskAssignmentUploadStatusQuery,
    'getTaskAssignmentUploadStatus',
    {
      upload_id: String(input.upload_id ?? input.uploadId ?? '').trim(),
    },
  )
}

/** PUT assignment CSV to the presigned upload_url (Content-Type: text/csv). */
export async function putCampaignAssignmentCsv(uploadUrl, assignments) {
  const url = String(uploadUrl ?? '').trim()
  if (!url) {
    return { ok: false, error: 'Upload URL missing from createCampaign response.' }
  }
  const csv = assignmentsToCsv(assignments)
  if (csv.trim().split('\n').length < 2) {
    return { ok: false, error: 'No assignment rows to upload.' }
  }

  try {
    const putResponse = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/csv' },
      body: csv,
    })
    if (!putResponse.ok) {
      return {
        ok: false,
        error: `Assignment upload failed (${putResponse.status}). Please try again.`,
      }
    }
    return { ok: true, error: '' }
  } catch (error) {
    return {
      ok: false,
      error: getApiMessage(error, 'Assignment upload failed'),
    }
  }
}

/**
 * Presigned PUT URL for a Static Voice raw .wav upload
 * (voice_source: UPLOADED_AUDIO). Mirrors the createCampaign/editCampaign
 * Excel-assignment upload_url pattern -- the returned s3_key becomes
 * voice_audio_raw_key on the next editCampaign save.
 */
export function fetchTaskVoiceAudioUploadUrl(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required.' })
  }
  return runCampaignsField(getTaskVoiceAudioUploadUrlMutation, 'getTaskVoiceAudioUploadUrl', {
    task_id,
  })
}

/**
 * Synthesizes/fetches a short Static Voice preview and returns it inline as
 * a base64 WAV data URL -- nothing written to S3 that outlives the request.
 * Save the campaign's Voice mode settings first; this always previews
 * whatever was last saved, not the unsaved form state.
 */
export function fetchTaskVoiceAudioPreviewUrl(input = {}) {
  const task_id = resolveCampaignId(input)
  if (!task_id) {
    return Promise.resolve({ status: 'ERROR', message: 'Campaign id is required.' })
  }
  return runCampaignsField(getTaskVoiceAudioPreviewUrlMutation, 'getTaskVoiceAudioPreviewUrl', {
    task_id,
  })
}

/** PUT the raw .wav file directly to the presigned upload_url (Content-Type: audio/wav). */
export async function putCampaignVoiceAudio(uploadUrl, file) {
  const url = String(uploadUrl ?? '').trim()
  if (!url) {
    return { ok: false, error: 'Upload URL missing from getTaskVoiceAudioUploadUrl response.' }
  }
  if (!file) {
    return { ok: false, error: 'No audio file selected.' }
  }
  try {
    const putResponse = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/wav' },
      body: file,
    })
    if (!putResponse.ok) {
      return {
        ok: false,
        error: `Audio upload failed (${putResponse.status}). Please try again.`,
      }
    }
    return { ok: true, error: '' }
  } catch (error) {
    return {
      ok: false,
      error: getApiMessage(error, 'Audio upload failed'),
    }
  }
}

/** Manually assign a campaign lead to a specific agent (MANUAL distribution). */
export function assignLeadToAgent(input = {}) {
  return runCampaignsField(assignLeadToAgentMutation, 'assignLeadToAgent', {
    task_id: resolveCampaignId(input),
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
    agent_id: String(input.agent_id ?? input.agentId ?? '').trim(),
  })
}

const BULK_ASSIGN_CHUNK_SIZE = 500

function normalizeBulkAssignmentEntry(row = {}) {
  return {
    request_id: String(row.request_id ?? row.requestId ?? '').trim(),
    agent_id: String(row.agent_id ?? row.agentId ?? '').trim(),
  }
}

function normalizeBulkAssignResponse(response) {
  const root =
    response?.data && typeof response.data === 'object' ? response.data : response
  const errorsRaw = Array.isArray(root?.errors) ? root.errors : []
  const succeeded = Number(root?.succeeded ?? root?.success_count)
  const failed = Number(root?.failed ?? root?.failed_count)
  return {
    status: response?.status ?? 'SUCCESS',
    message: response?.message,
    succeeded: Number.isFinite(succeeded) ? succeeded : null,
    failed: Number.isFinite(failed) ? failed : null,
    errors: errorsRaw.map((row) => ({
      request_id: String(row?.request_id ?? row?.requestId ?? '').trim(),
      agent_id: String(row?.agent_id ?? row?.agentId ?? '').trim(),
      error: String(row?.error ?? row?.message ?? 'Failed').trim(),
    })),
  }
}

/**
 * Bulk-assign leads for MANUAL campaigns (max 500 per API call).
 * Chunks client-side when needed and aggregates succeeded / failed / errors.
 */
export async function bulkAssignLeadsToAgents(input = {}) {
  const task_id = resolveCampaignId(input)
  const assignments = (Array.isArray(input.assignments) ? input.assignments : [])
    .map(normalizeBulkAssignmentEntry)
    .filter((row) => row.request_id && row.agent_id)

  if (!task_id) {
    return {
      status: 'ERROR',
      message: 'Campaign id is required.',
      succeeded: 0,
      failed: 0,
      errors: [],
    }
  }
  if (!assignments.length) {
    return {
      status: 'ERROR',
      message: 'Select at least one lead assignment.',
      succeeded: 0,
      failed: 0,
      errors: [],
    }
  }

  let succeeded = 0
  let failed = 0
  const errors = []
  let lastMessage = ''

  for (let i = 0; i < assignments.length; i += BULK_ASSIGN_CHUNK_SIZE) {
    const chunk = assignments.slice(i, i + BULK_ASSIGN_CHUNK_SIZE)
    const response = await runCampaignsField(
      bulkAssignLeadsToAgentsMutation,
      'bulkAssignLeadsToAgents',
      { task_id, assignments: chunk },
    )

    if (!isApiSuccess(response)) {
      failed += chunk.length
      lastMessage = getApiMessage(response, 'Bulk assign failed')
      for (const row of chunk) {
        errors.push({
          request_id: row.request_id,
          agent_id: row.agent_id,
          error: lastMessage,
        })
      }
      continue
    }

    const normalized = normalizeBulkAssignResponse(response)
    lastMessage = normalized.message || lastMessage
    if (normalized.succeeded != null) succeeded += normalized.succeeded
    else succeeded += Math.max(0, chunk.length - normalized.errors.length)
    if (normalized.failed != null) failed += normalized.failed
    else failed += normalized.errors.length
    errors.push(...normalized.errors)
  }

  return {
    status: failed && !succeeded ? 'ERROR' : 'SUCCESS',
    message:
      lastMessage ||
      `Assigned ${succeeded} lead${succeeded === 1 ? '' : 's'}${
        failed ? `, ${failed} failed` : ''
      }.`,
    succeeded,
    failed,
    errors,
  }
}

/** Status change for a lead inside a campaign (agent flow). */
export function updateMyCampaignLeadStatus(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
    request_id: String(input.request_id ?? input.requestId ?? '').trim(),
    request_status: String(input.request_status ?? input.requestStatus ?? '')
      .trim()
      .toUpperCase(),
    comments: String(input.comments ?? '').trim(),
  }
  const callUuid = String(input.call_uuid ?? input.callUuid ?? '').trim()
  if (callUuid) payload.call_uuid = callUuid

  // Optional: the caller asked to be called back on a specific date/time
  // (epoch ms). Same one-off mechanism the AI's scheduleCallbackTool uses
  // on live calls -- this is the human-agent equivalent.
  const callbackRequestedOn = Number(input.callback_requested_on ?? input.callbackRequestedOn)
  if (Number.isFinite(callbackRequestedOn) && callbackRequestedOn > Date.now()) {
    payload.callback_requested_on = callbackRequestedOn
    const callbackNote = String(input.callback_note ?? input.callbackNote ?? '').trim()
    if (callbackNote) payload.callback_note = callbackNote
  }

  const fieldsInput = input.fields
  if (typeof fieldsInput === 'string' && fieldsInput.trim()) {
    payload.fields = fieldsInput.trim()
  } else if (
    fieldsInput &&
    typeof fieldsInput === 'object' &&
    !Array.isArray(fieldsInput) &&
    Object.keys(fieldsInput).length
  ) {
    payload.fields = JSON.stringify(fieldsInput)
  }

  return runCampaignsField(
    updateMyTaskLeadStatusMutation,
    'updateMyTaskLeadStatus',
    payload,
  )
}

const BULK_STATUS_CHUNK_SIZE = 500

function normalizeBulkStatusUpdateEntry(row = {}) {
  const entry = {
    request_id: String(row.request_id ?? row.requestId ?? '').trim(),
    request_status: String(row.request_status ?? row.requestStatus ?? '')
      .trim()
      .toUpperCase(),
    comments: String(row.comments ?? '').trim(),
  }

  const fieldsInput = row.fields
  if (typeof fieldsInput === 'string' && fieldsInput.trim()) {
    entry.fields = fieldsInput.trim()
  } else if (
    fieldsInput &&
    typeof fieldsInput === 'object' &&
    !Array.isArray(fieldsInput) &&
    Object.keys(fieldsInput).length
  ) {
    entry.fields = JSON.stringify(fieldsInput)
  }

  return entry
}

function normalizeBulkStatusUpdateResponse(response) {
  const root =
    response?.data && typeof response.data === 'object' ? response.data : response
  const errorsRaw = Array.isArray(root?.errors) ? root.errors : []
  const succeeded = Number(root?.succeeded ?? root?.success_count)
  const failed = Number(root?.failed ?? root?.failed_count)
  return {
    status: response?.status ?? 'SUCCESS',
    message: response?.message,
    succeeded: Number.isFinite(succeeded) ? succeeded : null,
    failed: Number.isFinite(failed) ? failed : null,
    errors: errorsRaw.map((row) => ({
      request_id: String(row?.request_id ?? row?.requestId ?? '').trim(),
      request_status: String(
        row?.request_status ?? row?.requestStatus ?? '',
      )
        .trim()
        .toUpperCase(),
      error: String(row?.error ?? row?.message ?? 'Failed').trim(),
    })),
  }
}

/**
 * Bulk-update status for a specific list of campaign leads (max 500 / call).
 * Chunks client-side when needed. Requires ACTIVE campaign + date window.
 */
export async function bulkUpdateCampaignLeadStatus(input = {}) {
  const task_id = resolveCampaignId(input)
  const updates = (Array.isArray(input.updates) ? input.updates : [])
    .map(normalizeBulkStatusUpdateEntry)
    .filter((row) => row.request_id && row.request_status && row.comments)

  if (!task_id) {
    return {
      status: 'ERROR',
      message: 'Campaign id is required.',
      succeeded: 0,
      failed: 0,
      errors: [],
    }
  }
  if (!updates.length) {
    return {
      status: 'ERROR',
      message:
        'Select at least one lead and provide status and comments for each.',
      succeeded: 0,
      failed: 0,
      errors: [],
    }
  }

  let succeeded = 0
  let failed = 0
  const errors = []
  let lastMessage = ''

  for (let i = 0; i < updates.length; i += BULK_STATUS_CHUNK_SIZE) {
    const chunk = updates.slice(i, i + BULK_STATUS_CHUNK_SIZE)
    const response = await runCampaignsField(
      bulkUpdateTaskLeadStatusMutation,
      'bulkUpdateTaskLeadStatus',
      { task_id, updates: chunk },
    )

    if (!isApiSuccess(response)) {
      failed += chunk.length
      lastMessage = getApiMessage(response, 'Bulk status update failed')
      for (const row of chunk) {
        errors.push({
          request_id: row.request_id,
          request_status: row.request_status,
          error: lastMessage,
        })
      }
      continue
    }

    const normalized = normalizeBulkStatusUpdateResponse(response)
    lastMessage = normalized.message || lastMessage
    if (Number.isFinite(normalized.succeeded)) succeeded += normalized.succeeded
    else succeeded += chunk.length
    if (Number.isFinite(normalized.failed)) failed += normalized.failed
    if (normalized.errors?.length) errors.push(...normalized.errors)
  }

  const ok = failed === 0 && succeeded > 0
  return {
    status: ok ? 'SUCCESS' : succeeded > 0 ? 'PARTIAL' : 'ERROR',
    message:
      lastMessage ||
      `Updated ${succeeded} lead${succeeded === 1 ? '' : 's'}${
        failed ? `, ${failed} failed` : ''
      }.`,
    succeeded,
    failed,
    errors,
  }
}

/**
 * Apply one status + comments to every lead in the campaign (optional filters).
 * Async job — returns upload_id; poll via fetchCampaignAssignmentUploadStatus.
 * filters must match taskFilterInput: { field, operator, values: [String!]! }.
 */
export function bulkUpdateAllCampaignLeadStatus(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
    request_status: String(input.request_status ?? input.requestStatus ?? '')
      .trim()
      .toUpperCase(),
    comments: String(input.comments ?? '').trim(),
  }

  if (!payload.task_id) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Campaign id is required.',
    })
  }
  if (!payload.request_status || !payload.comments) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'Status and comments are required.',
    })
  }

  const filters = normalizeTaskFiltersForApi(input.filters)
  if (filters.length) payload.filters = filters

  return runCampaignsField(
    bulkUpdateAllTaskLeadStatusMutation,
    'bulkUpdateAllTaskLeadStatus',
    payload,
  )
}

/** taskFilterInput.values is [String!]! — coerce numbers/epochs to strings. */
function normalizeTaskFiltersForApi(filters = []) {
  if (!Array.isArray(filters)) return []
  return filters
    .map((row) => {
      const field = String(row?.field ?? '').trim()
      const operator = String(row?.operator ?? '').trim().toUpperCase()
      const values = (Array.isArray(row?.values) ? row.values : [])
        .map((value) => {
          if (value == null) return ''
          if (typeof value === 'object') {
            const nested = value.value ?? value.key ?? value.title
            return nested == null ? '' : String(nested)
          }
          return String(value)
        })
        .map((value) => value.trim())
        .filter(Boolean)
      if (!field || !operator || !values.length) return null
      return { field, operator, values }
    })
    .filter(Boolean)
}

/**
 * Poll getTaskAssignmentUploadStatus until COMPLETED / FAILED or timeout.
 * Shared by Excel assignment and bulkUpdateAllTaskLeadStatus jobs.
 */
export async function pollCampaignAssignmentUploadStatus(
  uploadId,
  { intervalMs = 2500, maxMs = 5 * 60 * 1000 } = {},
) {
  const id = String(uploadId ?? '').trim()
  if (!id) {
    return {
      uploadId: '',
      status: 'FAILED',
      timedOut: false,
      message: 'upload_id is required.',
    }
  }

  const started = Date.now()
  let lastPayload = null

  while (Date.now() - started < maxMs) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
    const response = await fetchCampaignAssignmentUploadStatus({ upload_id: id })
    if (!isApiSuccess(response)) continue

    const normalized = normalizeAssignmentUploadStatus(response)
    const status = String(normalized.status ?? '').trim().toUpperCase()
    const root =
      response?.data && typeof response.data === 'object' ? response.data : response
    lastPayload = {
      uploadId: normalized.uploadId || id,
      status,
      timedOut: false,
      message: response?.message || root?.message || '',
      matchedCount: normalized.matchedCount,
      unmatchedCount: normalized.unmatchedCount,
      alreadyPresentCount: normalized.alreadyPresentCount,
      raw: root,
    }
    if (status === 'COMPLETED' || status === 'FAILED') {
      return lastPayload
    }
  }

  return {
    uploadId: id,
    status: 'FAILED',
    timedOut: true,
    message:
      lastPayload?.message ||
      'Status update is still processing. Refresh the campaign in a moment.',
    raw: lastPayload?.raw ?? null,
  }
}

/** Kick off the AI dialer for an AI_AGENT campaign. */
export function runCampaignAICalls(input = {}) {
  return runCampaignsField(runTaskAICallsMutation, 'runTaskAICalls', {
    task_id: resolveCampaignId(input),
  })
}

/**
 * Campaign call / conversion metrics.
 * granularity: TOTAL | DAY | HOUR_OF_DAY | DAY_OF_WEEK | AGENT
 */
export function fetchCampaignMetrics(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
  }
  const granularity = String(input.granularity ?? 'TOTAL').trim().toUpperCase()
  if (granularity) payload.granularity = granularity

  // request_status values are free-text org config (e.g. "Interested"), not
  // fixed enums -- the backend matches them exactly, case-sensitively, so
  // this must NOT be uppercased (that was silently breaking every
  // conversion-rate/status match against real, non-uppercase status data).
  const convertedStatus = String(
    input.converted_status ?? input.convertedStatus ?? '',
  ).trim()
  if (convertedStatus) payload.converted_status = convertedStatus

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  // Admin picking one agent to inspect; a plain agent calling with no
  // agent_id is automatically scoped to themselves server-side, so this is
  // never required for a self-service view -- only for an org manager
  // wanting to zoom into one specific agent's numbers.
  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  return runCampaignsField(getTaskMetricsQuery, 'getTaskMetrics', payload)
}

/**
 * By Status / dispo breakdown -- live for "today"/no range, a daily
 * snapshot for a genuinely past date_to (see backend for why).
 */
export function fetchCampaignStatusMetrics(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
  }

  const convertedStatus = String(
    input.converted_status ?? input.convertedStatus ?? '',
  ).trim()
  if (convertedStatus) payload.converted_status = convertedStatus

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  return runCampaignsField(
    getTaskStatusMetricsQuery,
    'getTaskStatusMetrics',
    payload,
  )
}

/**
 * Kick off a background report export (call log / status history / agent
 * activity / lead detail). Returns immediately with an export_id -- the
 * actual file is built async and fetched via fetchReportExportStatus.
 */
export function startCampaignReportExport(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
    report_type: String(input.report_type ?? input.reportType ?? '').trim(),
  }
  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  return runCampaignsField(startReportExportQuery, 'startReportExport', payload)
}

/**
 * Product/Organization-scope equivalent of startCampaignReportExport above --
 * same startReportExport GraphQL field (its input type accepts EITHER
 * task_id OR scope_type/scope_id), just sends scope_type/scope_id instead
 * of a single task_id. fetchReportExportStatus below is unchanged and works
 * for both -- the export_id it polls doesn't care which shape started it.
 */
export function startScopeReportExport(input = {}) {
  const payload = {
    scope_type: String(input.scope_type ?? input.scopeType ?? '').trim(),
    scope_id: String(input.scope_id ?? input.scopeId ?? '').trim(),
    report_type: String(input.report_type ?? input.reportType ?? '').trim(),
  }
  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  return runCampaignsField(startReportExportQuery, 'startReportExport', payload)
}

/** Poll this until status is DONE (download_url present) or FAILED. */
export function fetchReportExportStatus(exportId) {
  return runCampaignsField(getReportExportStatusQuery, 'getReportExportStatus', {
    export_id: String(exportId ?? '').trim(),
  })
}

/**
 * KAN-32: save the whole report_digest_config object (all 4 report types)
 * in one call -- ReportDigestSettings.vue has one Save button covering all
 * of them, same as DashboardDigestSettings.vue's own single-object save.
 *
 * report_digest_config is typed AWSJSON! in the schema -- unlike a plain
 * String/Int field, AppSync requires an AWSJSON *variable* to arrive as an
 * already-JSON-encoded string, not a native object; sending the raw object
 * fails GraphQL variable coercion with "has an invalid value" before the
 * request ever reaches the resolver (confirmed live 2026-08-19: every save
 * from the real UI was silently failing this way -- the request returned
 * HTTP 200 with a GraphQL-level error, and DynamoDB was never touched).
 */
export function updateTaskReportDigestConfig(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
    report_digest_config: JSON.stringify(input.report_digest_config ?? input.reportDigestConfig ?? {}),
  }
  return runCampaignsField(updateTaskReportDigestConfigMutation, 'updateTaskReportDigestConfig', payload)
}

/**
 * "Send now" for one report type on this campaign -- fires an immediate
 * send using whatever is CURRENTLY SAVED, so call this only after
 * updateTaskReportDigestConfig's own save has already succeeded, same
 * caveat as triggerDashboardDigestNow.
 */
export function triggerReportDigestNow(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
    report_type: String(input.report_type ?? input.reportType ?? '').trim(),
  }
  return runCampaignsField(triggerReportDigestNowMutation, 'triggerReportDigestNow', payload)
}

/**
 * Drill into a metrics bucket (leads/calls behind a number).
 * source: calls | history | both
 */
export function fetchCampaignMetricsDrilldown(input = {}) {
  const payload = {
    task_id: resolveCampaignId(input),
  }

  const source = String(input.source ?? '').trim().toLowerCase()
  if (source) payload.source = source

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  if (input.hour != null && input.hour !== '') {
    const hour = Number(input.hour)
    if (Number.isFinite(hour)) payload.hour = Math.floor(hour)
  }

  const dayOfWeek = String(
    input.day_of_week ?? input.dayOfWeek ?? '',
  ).trim()
  if (dayOfWeek) payload.day_of_week = dayOfWeek

  // Same case-sensitivity note as converted_status above -- never uppercase.
  const requestStatus = String(
    input.request_status ?? input.requestStatus ?? '',
  ).trim()
  if (requestStatus) payload.request_status = requestStatus

  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  const callOutcome = String(input.call_outcome ?? input.callOutcome ?? '').trim()
  if (callOutcome) payload.call_outcome = callOutcome

  const callDuration = String(input.call_duration ?? input.callDuration ?? '').trim()
  if (callDuration) payload.call_duration = callDuration

  return runCampaignsField(
    getTaskMetricsDrilldownQuery,
    'getTaskMetricsDrilldown',
    payload,
  )
}

/**
 * Product/Organization-scope equivalents of fetchCampaignMetrics/
 * fetchCampaignStatusMetrics/fetchCampaignMetricsDrilldown above -- same
 * payload shape, just scope_type/scope_id instead of task_id, hitting the
 * generalized getScopeMetrics/getScopeStatusMetrics/getScopeMetricsDrilldown
 * backend commands instead of the single-campaign ones. CampaignDashboardPanel.vue
 * picks between the two families based on its scopeType prop -- Campaign
 * scope keeps using the functions above, unchanged.
 */
export function fetchScopeMetrics(input = {}) {
  const payload = {
    scope_type: String(input.scope_type ?? input.scopeType ?? '').trim(),
    scope_id: String(input.scope_id ?? input.scopeId ?? '').trim(),
  }
  const granularity = String(input.granularity ?? 'TOTAL').trim().toUpperCase()
  if (granularity) payload.granularity = granularity

  const convertedStatus = String(
    input.converted_status ?? input.convertedStatus ?? '',
  ).trim()
  if (convertedStatus) payload.converted_status = convertedStatus

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  return runCampaignsField(getScopeMetricsQuery, 'getScopeMetrics', payload)
}

export function fetchScopeStatusMetrics(input = {}) {
  const payload = {
    scope_type: String(input.scope_type ?? input.scopeType ?? '').trim(),
    scope_id: String(input.scope_id ?? input.scopeId ?? '').trim(),
  }

  const convertedStatus = String(
    input.converted_status ?? input.convertedStatus ?? '',
  ).trim()
  if (convertedStatus) payload.converted_status = convertedStatus

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  return runCampaignsField(
    getScopeStatusMetricsQuery,
    'getScopeStatusMetrics',
    payload,
  )
}

export function fetchScopeMetricsDrilldown(input = {}) {
  const payload = {
    scope_type: String(input.scope_type ?? input.scopeType ?? '').trim(),
    scope_id: String(input.scope_id ?? input.scopeId ?? '').trim(),
  }

  const source = String(input.source ?? '').trim().toLowerCase()
  if (source) payload.source = source

  const dateFrom = String(input.date_from ?? input.dateFrom ?? '').trim()
  if (dateFrom) payload.date_from = dateFrom
  const dateTo = String(input.date_to ?? input.dateTo ?? '').trim()
  if (dateTo) payload.date_to = dateTo

  if (input.hour != null && input.hour !== '') {
    const hour = Number(input.hour)
    if (Number.isFinite(hour)) payload.hour = Math.floor(hour)
  }

  const dayOfWeek = String(
    input.day_of_week ?? input.dayOfWeek ?? '',
  ).trim()
  if (dayOfWeek) payload.day_of_week = dayOfWeek

  const requestStatus = String(
    input.request_status ?? input.requestStatus ?? '',
  ).trim()
  if (requestStatus) payload.request_status = requestStatus

  const agentId = String(input.agent_id ?? input.agentId ?? '').trim()
  if (agentId) payload.agent_id = agentId

  const callOutcome = String(input.call_outcome ?? input.callOutcome ?? '').trim()
  if (callOutcome) payload.call_outcome = callOutcome

  const callDuration = String(input.call_duration ?? input.callDuration ?? '').trim()
  if (callDuration) payload.call_duration = callDuration

  return runCampaignsField(
    getScopeMetricsDrilldownQuery,
    'getScopeMetricsDrilldown',
    payload,
  )
}

/** Runs a documented public-API endpoint through the portal's server-side
 *  proxy, so the browser never handles OAuth credentials. */
export async function runPublicApiTest({ path, method, body }) {
  try {
    const { data, errors } = await client.graphql({
      query: runPublicApiTestMutation,
      variables: { input: { path, method, body: JSON.stringify(body ?? {}) } },
    })
    if (errors?.length) throw new Error(errors[0].message)
    return parseAwsJson(data?.runPublicApiTest)
  } catch (error) {
    return { status: 'ERROR', message: getApiMessage(error, 'Request failed') }
  }
}
